import { memo, useCallback, useRef, useState } from 'react';

import { useTrackCanvasDeleted } from '@air/analytics';
import { ConfirmationModal } from '@air/component-confirmation-modal';
import { useToasts } from '@air/provider-toast';

import { useSelectedCanvasId } from '~/hooks/canvases/useSelectedCanvasId';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateCanvasesDeleteCanvasMutation } from '~/swr-hooks/canvases/useDeleteCanvas';
import {
  beginOwnCanvasDeleteRedirect,
  clearOwnCanvasDeleteRedirect,
  getDeletedCanvasesSuccessMessage,
  showCanvasDeletedToast,
} from '~/utils/canvases/ownCanvasDeleteRedirect';

type BooleanRef = {
  readonly current: boolean;
};

/** Biome cannot see writes to `ref.current`, so it treats the initial value as constant. */
const isRefTrue = (ref: BooleanRef) => ref.current;

/** The dedicated canvas page supplies this. List and split-panel deletes omit it and stay put. */
export type LeaveAfterCanvasDelete = {
  canvasId: string;
  leave: () => Promise<unknown>;
};

export type DeleteCanvasConfirmationModalProps = {
  boardId?: string;
  canvasIds: string[];
  leaveAfterDelete?: LeaveAfterCanvasDelete;
  onDelete?: () => void;
};

export const DeleteCanvasConfirmationModal = memo(
  ({ boardId, canvasIds, leaveAfterDelete, onDelete, onClose }: AirModalProps<DeleteCanvasConfirmationModalProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { mutateAsync: deleteCanvas, isPending } = usePrivateCanvasesDeleteCanvasMutation();
    const { selectedCanvasId, closeCanvas } = useSelectedCanvasId();
    const { trackCanvasDeleted } = useTrackCanvasDeleted();
    const { showToast } = useToasts();
    // `isPending` drops when the mutation settles, before the page finishes leaving. Keep the
    // button disabled through that gap so a second click cannot fail and clear the redirect.
    const isSubmittingRef = useRef<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = useCallback(async () => {
      if (isRefTrue(isSubmittingRef) || canvasIds.length === 0 || !currentWorkspace?.id) {
        return;
      }

      isSubmittingRef.current = true;
      setIsSubmitting(true);

      const pageLeave =
        leaveAfterDelete && canvasIds.includes(leaveAfterDelete.canvasId) ? leaveAfterDelete : undefined;

      // Mark the open page before the mutation drops its detail query, so the editor
      // does not paint "This Canvas no longer exists" while we leave for the index.
      if (pageLeave) {
        beginOwnCanvasDeleteRedirect(pageLeave.canvasId);
      }

      try {
        await deleteCanvas({ workspaceId: currentWorkspace.id, canvasIds });
      } catch {
        if (pageLeave) {
          clearOwnCanvasDeleteRedirect(pageLeave.canvasId);
        }

        isSubmittingRef.current = false;
        setIsSubmitting(false);

        // useDeleteCanvas shows toast on error
        return;
      }

      for (const canvasId of canvasIds) {
        trackCanvasDeleted({
          canvas_id: canvasId,
          board_id: boardId,
          location: boardId ? 'board' : 'canvases',
        });
      }

      if (selectedCanvasId && canvasIds.includes(selectedCanvasId)) {
        closeCanvas();
      }

      await showCanvasDeletedToast({
        message: getDeletedCanvasesSuccessMessage(canvasIds.length),
        onRedirect: pageLeave
          ? async () => {
              try {
                await pageLeave.leave();
              } catch {
                clearOwnCanvasDeleteRedirect(pageLeave.canvasId);
              }
            }
          : undefined,
        showToast,
      });

      onDelete?.();
      onClose();
    }, [
      boardId,
      canvasIds,
      closeCanvas,
      currentWorkspace,
      deleteCanvas,
      leaveAfterDelete,
      onClose,
      onDelete,
      selectedCanvasId,
      showToast,
      trackCanvasDeleted,
    ]);

    const count = canvasIds.length;
    const title = count === 1 ? 'Delete this Canvas?' : `Delete ${count} Canvases?`;
    const description =
      count === 1
        ? 'This action cannot be undone. All content on this Canvas will be permanently deleted.'
        : `This action cannot be undone. All content on these ${count} Canvases will be permanently deleted.`;
    const isDeleting = isPending || isSubmitting;

    return (
      <ConfirmationModal
        id="delete-canvas-confirmation"
        isOpen
        onDismiss={onClose}
        title={title}
        description={description}
        ctaPrimaryText={isDeleting ? 'Deleting...' : 'Delete'}
        onConfirm={handleConfirm}
        isDangerous
        isLoading={isDeleting}
        isDisabled={isDeleting}
      />
    );
  },
);

DeleteCanvasConfirmationModal.displayName = 'DeleteCanvasConfirmationModal';
